import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import axios from "axios";
import RouteToaster from "@/components/RouteToaster";
import { toast } from "sonner";
// framer-motion removed from App-level transitions for reliability

// Import screens
import LoginScreen from "@/components/LoginScreen";
import DashboardScreen from "@/components/DashboardScreen";
import UploadScreen from "@/components/UploadScreen";
import GenerationScreen from "@/components/GenerationScreen";
import StoriesScreen from "@/components/StoriesScreen";
import PreviewScreen from "@/components/PreviewScreen";
import BDDashboard from "@/components/bd/BDDashboard";
import BDChat from "@/components/bd/BDChat";
import MinisiteDashboard from "@/components/minisite/MinisiteDashboard";
import MinisiteEditor from "@/components/minisite/MinisiteEditor";
import AgentChat from "@/components/agent/AgentChat";
import FormulaireClient from "@/components/formulaire/FormulaireClient";
import MyRequests from "@/components/agent/MyRequests";
import Deliveries from "@/components/agent/Deliveries";

const BD_ALLOWED_EMAILS = [
  "undeuxb@gmail.com",
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "quentin.tousart@webpulser.com",
];

// Miroir de EMAIL_TO_PERSONA / TEAM_PERSONAS cote backend (agent/contexts.py) :
// c'est cette liste-la que `is_team_email` applique sur /api/agent/deliveries.
// Elle coincide aujourd'hui avec BD_ALLOWED_EMAILS, mais les deux ne disent pas
// la meme chose : ouvrir la BD a quelqu'un ne doit pas lui afficher un bouton
// qui repondra 403.
const TEAM_EMAILS = [
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "undeuxb@gmail.com",
  "quentin.tousart@webpulser.com",
];

const ALLOWED_MINISITE_EMAILS = [
  "undeuxb@gmail.com",
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "quentin.tousart@webpulser.com",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// App states
const STATES = {
  LOGIN: "login",
  DASHBOARD: "dashboard",
  UPLOAD: "upload",
  GENERATING: "generating",
  STORIES: "stories",
  PREVIEW: "preview",
};

function App() {
  const [appState, setAppState] = useState(STATES.LOGIN);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('autobio_token'));
  // sessionRestoring covers the async axios /auth/me call below. While true,
  // route guards must NOT redirect — otherwise a refresh on /bd/* bounces to /
  // even when the user has a valid token in localStorage.
  const [sessionRestoring, setSessionRestoring] = useState(
    () => !!localStorage.getItem('autobio_token')
  );
  const [sessionId, setSessionId] = useState(null);
  // Champs du formulaire client à reporter sur l'écran Sources (évite la ressaisie)
  const [uploadPrefill, setUploadPrefill] = useState(null);
  const [autobiography, setAutobiography] = useState("");
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [stories, setStories] = useState([]);
  const [detectedStyle, setDetectedStyle] = useState(null);
  const [promptVariants, setPromptVariants] = useState([]);
  const [defaultPromptVariantId, setDefaultPromptVariantId] = useState("classique");
  const [selectedPromptVariantId, setSelectedPromptVariantId] = useState("classique");

  // Fetch available prompt variants (once user is logged in)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/prompt-variants`);
        if (cancelled) return;
        const variants = res.data?.variants || [];
        setPromptVariants(variants);
        const def = res.data?.default || "classique";
        setDefaultPromptVariantId(def);
        setSelectedPromptVariantId((current) => {
          // If the current selection is still valid, keep it. Otherwise use the default.
          if (variants.some((v) => v.id === current)) return current;
          return def;
        });
      } catch (err) {
        // Non-blocking : fallback to default classique behavior if endpoint is unreachable
        console.warn("Could not fetch prompt variants", err);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Axios interceptor for JWT
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem('autobio_token');
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('autobio_token');
          localStorage.removeItem('autobio_sessionId');
          setToken(null);
          setUser(null);
          setAppState(STATES.LOGIN);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('autobio_token');
    if (!savedToken) {
      setAppState(STATES.LOGIN);
      setSessionRestoring(false);
      return;
    }

    const restoreSession = async () => {
      try {
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data.user);
        setToken(savedToken);

        const savedSessionId = localStorage.getItem('autobio_sessionId');
        if (!savedSessionId) {
          setAppState(STATES.DASHBOARD);
          return;
        }

        const res = await axios.get(`${API}/session/${savedSessionId}`);
        const session = res.data;
        setSessionId(savedSessionId);

        switch (session.status) {
          case 'ready':
            setAutobiography(session.autobiography || '');
            setChatHistory(session.chat_history || []);
            setVersions(session.versions || []);
            setCurrentVersion(session.current_version || 1);
            setAppState(STATES.PREVIEW);
            break;
          case 'extracting':
          case 'generating':
            setAppState(STATES.GENERATING);
            break;
          case 'stories_ready': {
            const storiesRes = await axios.get(`${API}/stories/${savedSessionId}`);
            setStories(storiesRes.data.stories || []);
            setDetectedStyle(storiesRes.data.style || null);
            setAppState(STATES.STORIES);
            break;
          }
          default:
            setAppState(STATES.DASHBOARD);
            break;
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
        localStorage.removeItem('autobio_token');
        localStorage.removeItem('autobio_sessionId');
        setAppState(STATES.LOGIN);
      } finally {
        setSessionRestoring(false);
      }
    };

    restoreSession();
  }, []);

  // Persist sessionId to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('autobio_sessionId', sessionId);
    } else {
      localStorage.removeItem('autobio_sessionId');
    }
  }, [sessionId]);

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('autobio_token', response.data.token);
        setToken(response.data.token);
        setUser(response.data.user);
        setAppState(STATES.DASHBOARD);
        toast.success("Connexion réussie");
        return true;
      } else {
        toast.error(response.data.message || "Identifiants incorrects");
        return false;
      }
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
      return false;
    }
  };

  const handleSelectClient = async (clientSessionId) => {
    setSessionId(clientSessionId);
    localStorage.setItem('autobio_sessionId', clientSessionId);

    try {
      const res = await axios.get(`${API}/session/${clientSessionId}`);
      const session = res.data;

      // Posé pour TOUS les statuts (pas seulement le cas UPLOAD) : si une
      // erreur de génération renvoie plus tard sur l'écran Sources, il doit
      // porter les données de CE dossier, pas celles du précédent.
      setUploadPrefill({
        prenomNom: session.prenom_nom || "",
        activite: session.activite || "",
        publicCible: session.public_cible || "",
      });

      switch (session.status) {
        case 'ready':
          setAutobiography(session.autobiography || '');
          setChatHistory(session.chat_history || []);
          setVersions(session.versions || []);
          setCurrentVersion(session.current_version || 1);
          setAppState(STATES.PREVIEW);
          break;
        case 'stories_ready': {
          const storiesRes = await axios.get(`${API}/stories/${clientSessionId}`);
          setStories(storiesRes.data.stories || []);
          setDetectedStyle(storiesRes.data.style || null);
          setAppState(STATES.STORIES);
          break;
        }
        case 'extracting':
        case 'generating':
          setAppState(STATES.GENERATING);
          break;
        default:
          // Dossier au stade des sources : le formulaire client pré-remplit
          // l'écran (setUploadPrefill ci-dessus), l'équipe ne retape rien.
          setAppState(STATES.UPLOAD);
          break;
      }
    } catch (err) {
      toast.error("Erreur lors du chargement du dossier");
    }
  };

  const handleNewClient = () => {
    setSessionId(null);
    setAutobiography("");
    setChatHistory([]);
    setVersions([]);
    setCurrentVersion(1);
    setStories([]);
    setDetectedStyle(null);
    setUploadPrefill(null);
    localStorage.removeItem('autobio_sessionId');
    setAppState(STATES.UPLOAD);
  };

  const handleLogout = () => {
    localStorage.removeItem('autobio_token');
    localStorage.removeItem('autobio_sessionId');
    setToken(null);
    setUser(null);
    setSessionId(null);
    setAppState(STATES.LOGIN);
  };

  // Upload files and text
  const handleUpload = async (files, pastedText, metadata) => {
    const formData = new FormData();
    if (sessionId) {
      formData.append("session_id", sessionId);
    }
    formData.append("pasted_text", pastedText || "");

    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!uploadRes.data.success) {
        toast.error(uploadRes.data.error || "Erreur lors du téléversement");
        return;
      }

      const activeSessionId = uploadRes.data.session_id || sessionId;
      setSessionId(activeSessionId);
      localStorage.setItem('autobio_sessionId', activeSessionId);

      toast.success(uploadRes.data.message);

      // Start generation
      const genFormData = new FormData();
      genFormData.append("session_id", activeSessionId);
      genFormData.append("prenom_nom", metadata.prenomNom || "[Prénom Nom]");
      genFormData.append("activite", metadata.activite || "[Activité professionnelle]");
      genFormData.append("public_cible", metadata.publicCible || "[Public cible]");
      genFormData.append("instructions_supp", metadata.instructionsSupp || "");

      const genRes = await axios.post(`${API}/generate`, genFormData);

      if (genRes.data.success) {
        setAppState(STATES.GENERATING);
        setProgress(0);
        setStatusMessage("Démarrage...");
      } else {
        toast.error(genRes.data.error || "Erreur lors du démarrage");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du téléversement");
    }
  };

  // Poll for progress
  useEffect(() => {
    if (appState !== STATES.GENERATING || !sessionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/progress/${sessionId}`);
        setProgress(res.data.progress);
        setStatusMessage(res.data.message);

        if (res.data.status === "stories_ready") {
          clearInterval(pollInterval);
          try {
            const storiesRes = await axios.get(`${API}/stories/${sessionId}`);
            setStories(storiesRes.data.stories || []);
            setDetectedStyle(storiesRes.data.style || null);
            setAppState(STATES.STORIES);
          } catch (stErr) {
            console.error("Failed to load stories:", stErr);
            toast.error("Erreur lors du chargement des histoires");
            setAppState(STATES.UPLOAD);
          }
        } else if (res.data.status === "ready") {
          setAutobiography(res.data.autobiography);
          setAppState(STATES.PREVIEW);
          clearInterval(pollInterval);
          toast.success("Autobiographie générée !");
          // Load versions
          loadVersions();
        } else if (res.data.status === "error") {
          clearInterval(pollInterval);
          toast.error(res.data.message || "Erreur lors de la génération");
          setAppState(STATES.UPLOAD);
        }
      } catch (error) {
        console.error(error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [appState, sessionId]);

  // Load versions
  const loadVersions = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/versions/${sessionId}`);
      setVersions(res.data.versions || []);
      setCurrentVersion(res.data.current_version || 1);
    } catch (error) {
      console.error("Error loading versions:", error);
    }
  };

  // Handle chat message (conversation mode - doesn't regenerate)
  const handleChatMessage = async (message) => {
    if (!sessionId || !message.trim()) return;

    setIsAdjusting(true);
    setChatHistory((prev) => [...prev, { role: "user", text: message }]);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: message,
      });

      if (res.data.success) {
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", text: res.data.response },
        ]);
      } else {
        toast.error(res.data.error || "Erreur");
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", text: `Erreur : ${res.data.error}` },
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setIsAdjusting(false);
    }
  };

  // Regenerate with all chat context
  const handleRegenerateWithContext = async () => {
    if (!sessionId) return;

    setIsAdjusting(true);

    try {
      const res = await axios.post(`${API}/regenerate-with-context`, {
        session_id: sessionId,
        prompt_variant_id: selectedPromptVariantId,
      });

      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version);
        setChatHistory([{ role: "assistant", text: `✓ Version ${res.data.version} générée avec vos retours.` }]);
        toast.success("Autobiographie régénérée !");
        loadVersions();
      } else {
        toast.error(res.data.error || "Erreur lors de la régénération");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la régénération");
    } finally {
      setIsAdjusting(false);
    }
  };

  // Legacy: Direct adjustment (quick modifications)
  const handleDirectAdjustment = async (demande) => {
    if (!sessionId || !demande.trim()) return;

    setIsAdjusting(true);
    setChatHistory((prev) => [...prev, { role: "user", text: demande }]);

    try {
      const res = await axios.post(`${API}/adjust`, {
        session_id: sessionId,
        demande: demande,
      });

      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version || currentVersion + 1);
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", text: "Modification appliquée." },
        ]);
        toast.success("Modification appliquée");
        loadVersions();
      } else {
        toast.error(res.data.error || "Erreur lors de la modification");
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", text: `Erreur : ${res.data.error}` },
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la modification");
    } finally {
      setIsAdjusting(false);
    }
  };

  // Restore a previous version
  const handleRestoreVersion = async (versionNum) => {
    if (!sessionId) return;
    
    try {
      const res = await axios.post(`${API}/restore-version`, {
        session_id: sessionId,
        version: versionNum,
      });
      
      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version);
        toast.success(`Version ${versionNum} restaurée`);
      } else {
        toast.error(res.data.error || "Erreur lors de la restauration");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la restauration");
    }
  };

  // Copy to clipboard with fallback
  const handleCopy = useCallback(() => {
    // Fallback method using textarea
    const textArea = document.createElement("textarea");
    textArea.value = autobiography;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success("Texte copié dans le presse-papiers");
    } catch (err) {
      // Try modern API as backup
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(autobiography)
          .then(() => toast.success("Texte copié dans le presse-papiers"))
          .catch(() => toast.error("Impossible de copier. Sélectionnez le texte manuellement."));
      } else {
        toast.error("Impossible de copier. Sélectionnez le texte manuellement.");
      }
    }
    
    document.body.removeChild(textArea);
  }, [autobiography]);

  // Download as PDF
  const handleDownloadPdf = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/download-pdf/${sessionId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "autobiographie.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  }, [sessionId]);

  // Download as .txt - using data URI which works better cross-browser
  const handleDownload = useCallback(() => {
    if (!autobiography) return;
    
    // Use data URI approach for better compatibility
    const dataUri = 'data:text/plain;charset=utf-8,' + encodeURIComponent(autobiography);
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', 'autobiographie.txt');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Téléchargement lancé");
  }, [autobiography]);

  // Generate from selected stories
  const handleGenerateFromStories = async (selection) => {
    try {
      setAppState(STATES.GENERATING);
      setProgress(50);
      setStatusMessage("Rédaction de l'autobiographie...");
      await axios.post(`${API}/generate-from-stories`, {
        session_id: sessionId,
        prompt_variant_id: selectedPromptVariantId,
        ...selection,
      });
      // Polling resumes automatically via the useEffect
    } catch (err) {
      toast.error("Erreur lors du lancement de la génération");
      setAppState(STATES.STORIES);
    }
  };

  // Save inline edit
  const handleSaveEdit = async (newContent) => {
    try {
      const res = await axios.post(`${API}/save-edit/${sessionId}`, {
        content: newContent,
      });
      setAutobiography(newContent);
      setCurrentVersion(res.data.version);
      await loadVersions();
      toast.success("Modifications sauvegardees");
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleBackToDashboard = useCallback(({ confirmGenerating = false } = {}) => {
    if (confirmGenerating) {
      const confirmed = window.confirm(
        "Une génération est en cours. Revenir au tableau de bord arrêtera seulement le suivi à l'écran ; la génération continuera côté serveur. Continuer ?"
      );
      if (!confirmed) return false;
    }
    setAppState(STATES.DASHBOARD);
    return true;
  }, []);

  const userEmail = (user?.email || "").toLowerCase();
  const canAccessBD = BD_ALLOWED_EMAILS.includes(userEmail);
  const isTeam = TEAM_EMAILS.includes(userEmail);
  const canAccessMinisite = ALLOWED_MINISITE_EMAILS.includes(userEmail);

  const homeUI = (
    <div className="min-h-screen bg-background">
      {appState === STATES.DASHBOARD && canAccessBD && (
        <div className="fixed top-4 right-4 z-50">
          <Link
            to="/bd"
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium shadow hover:opacity-90"
          >
            Module BD
          </Link>
        </div>
      )}

      {appState === STATES.DASHBOARD && canAccessMinisite && (
        <Link
          to="/minisite"
          style={{
            position: "fixed",
            bottom: 90,
            right: 24,
            background: "#0ea5e9",
            color: "white",
            border: "none",
            borderRadius: 30,
            padding: "12px 20px",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 600,
            textDecoration: "none",
            zIndex: 50,
          }}
        >
          Module Minisites
        </Link>
      )}

      {appState === STATES.LOGIN && (
        <LoginScreen onLogin={handleLogin} />
      )}
      {appState === STATES.DASHBOARD && (
        <DashboardScreen
          user={user}
          isTeam={isTeam}
          onSelectClient={handleSelectClient}
          onNewClient={handleNewClient}
          onLogout={handleLogout}
        />
      )}
      {appState === STATES.UPLOAD && (
        <UploadScreen
          onGenerate={handleUpload}
          initialInfo={uploadPrefill}
          onBackToDashboard={handleBackToDashboard}
        />
      )}
      {appState === STATES.STORIES && (
        <StoriesScreen
          stories={stories}
          style={detectedStyle}
          onGenerate={handleGenerateFromStories}
          onBackToDashboard={handleBackToDashboard}
          isLoading={false}
          promptVariants={promptVariants}
          selectedPromptVariantId={selectedPromptVariantId}
          onPromptVariantChange={setSelectedPromptVariantId}
          defaultPromptVariantId={defaultPromptVariantId}
        />
      )}
      {appState === STATES.GENERATING && (
        <GenerationScreen
          progress={progress}
          statusMessage={statusMessage}
          onBackToDashboard={() => handleBackToDashboard({ confirmGenerating: true })}
        />
      )}
      {appState === STATES.PREVIEW && (
        <PreviewScreen
          autobiography={autobiography}
          chatHistory={chatHistory}
          onChatMessage={handleChatMessage}
          onRegenerateWithContext={handleRegenerateWithContext}
          onDirectAdjust={handleDirectAdjustment}
          onCopy={handleCopy}
          onBackToDashboard={handleBackToDashboard}
          isAdjusting={isAdjusting}
          versions={versions}
          currentVersion={currentVersion}
          onRestoreVersion={handleRestoreVersion}
          onSaveEdit={handleSaveEdit}
          onDownloadPdf={handleDownloadPdf}
          promptVariants={promptVariants}
          selectedPromptVariantId={selectedPromptVariantId}
          onPromptVariantChange={setSelectedPromptVariantId}
        />
      )}
    </div>
  );

  // Defense in depth : even though the backend rejects unauthorized callers,
  // we hide the module UI to avoid leaking client names to non-allowed users
  // who could otherwise type the URL directly.
  // While the session is being restored from localStorage, render null so the
  // user does NOT get bounced to / on a refresh of /bd/* or /minisite/*.
  const guardModule = (el, canAccess) => {
    if (sessionRestoring) return null;
    if (!user) return <Navigate to="/" replace />;
    if (!canAccess) return <Navigate to="/" replace />;
    return el;
  };
  const requireBD = (el) => guardModule(el, canAccessBD);
  const requireMinisite = (el) => guardModule(el, canAccessMinisite);

  const requireAuth = (el) => {
    if (sessionRestoring) return null;
    if (!user) return <Navigate to="/" replace />;
    return el;
  };

  return (
    <BrowserRouter>
      <RouteToaster />
      <Routes>
        <Route path="/bd" element={requireBD(<BDDashboard token={token} onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/bd/:clientId" element={requireBD(<BDChat token={token} onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/minisite" element={requireMinisite(<MinisiteDashboard onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/minisite/:clientId" element={requireMinisite(<MinisiteEditor onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/mes-demandes" element={requireAuth(<MyRequests token={token} />)} />
        <Route path="/nouveautes" element={requireAuth(<Deliveries token={token} onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/assistant" element={requireAuth(<AgentChat token={token} onBackToDashboard={handleBackToDashboard} />)} />
        <Route path="/assistant/:contextName" element={requireAuth(<AgentChat token={token} onBackToDashboard={handleBackToDashboard} />)} />
        {/* Formulaire client : route PUBLIQUE (sans login, accès par token) */}
        <Route path="/formulaire/:token" element={<FormulaireClient />} />
        <Route path="*" element={homeUI} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
